import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './UserDetail.module.css';
import { useApi } from '../../../hooks/ApiProvider';
import Confirm from '../../Modals/Confirm';
import PDFViewer from '../../PDFViewer/PDFViewer';
import { EditIcon, RegenerateIcon, DeleteIcon, SaveIcon, CancelIcon, BookIcon, EditBookIcon, ChevronLeftIcon, ChevronRightIcon } from '../../../assets/icons';


interface Section {
  index: number;
  title: string;
  paragraphs: string[];
}

interface BookContents {
  sections: Section[];
  title: string;
  author: string;
  pdf_download_url?: string;
}

interface PhysicalBookOrderSimple {
  id: number;
  delivery_state: string;
  created_at?: string;
  updated_at?: string;
  cover_style_id?: string;
  cover_image_url?: string | null;
}

// Toast 컴포넌트 인터페이스
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  isVisible: boolean;
  onClose: () => void;
}

// Toast 컴포넌트
const Toast: React.FC<ToastProps> = ({ message, type, isVisible, onClose }) => {
  if (!isVisible) return null;

  const getToastStyle = () => {
    const baseStyle = {
      position: 'fixed' as const,
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '8px',
      color: 'white',
      fontSize: '14px',
      fontWeight: '500',
      zIndex: 1000,
      maxWidth: '300px',
      wordWrap: 'break-word' as const,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      animation: 'slideIn 0.3s ease-out'
    };

    switch (type) {
      case 'success':
        return { ...baseStyle, backgroundColor: '#10b981' };
      case 'error':
        return { ...baseStyle, backgroundColor: '#ef4444' };
      case 'info':
        return { ...baseStyle, backgroundColor: '#3b82f6' };
      default:
        return { ...baseStyle, backgroundColor: '#6b7280' };
    }
  };

  return (
    <div style={getToastStyle()}>
      {message}
      <button
        onClick={onClose}
        style={{
          marginLeft: '10px',
          background: 'none',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          fontSize: '16px'
        }}
      >
        ×
      </button>
    </div>
  );
};

interface Book {
  id: number;
  project_id: number;
  job_id: string;
  status: string;
  contents: BookContents;
  created_at: string;
  updated_at: string;
  physical_book_order?: PhysicalBookOrderSimple;
}

const DigitalbookDetail: React.FC = () => {
  // CSS 애니메이션 스타일 추가
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const { userId, projectId, bookId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getDigitalBookInfo, deleteDigitalBook, rewriteDigitalBook, editDigitalBookSection, orderPhysicalBook, downloadBookContents, genDigitalBookPDF } = useApi();
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toastIsVisible, setToastIsVisible] = useState<boolean>(false);

  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<number | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [selectedSection, setSelectedSection] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(-2); // -2는 표지, -1은 목차, 0 이상은 본문 페이지
  const [pagesPerSection, setPagesPerSection] = useState<number>(1);
  const [isLandscape, setIsLandscape] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  
  const [showRewriteConfirm, setShowRewriteConfirm] = useState<boolean>(false);
  const [showCreatePhysicalBookOrderModal, setShowCreatePhysicalBookOrderModal] = useState<boolean>(false);
  const [createOrderPasscode, setCreateOrderPasscode] = useState<string>('');
  const [createOrderPasscodeInput, setCreateOrderPasscodeInput] = useState<string>('');
  const [passcode, setPasscode] = useState<string>('');
  const [passcodeError, setPasscodeError] = useState<string>('');
  const [generatedPasscode, setGeneratedPasscode] = useState<string>('');
  const [showFullscreenModal, setShowFullscreenModal] = useState<boolean>(false);
  const [pdfKey, setPdfKey] = useState<number>(0); // PDF 뷰어 완전 재마운트용

  // selectedSection가 유효한 범위 내에 있는지 확인
  useEffect(() => {
    if (book?.contents?.sections && selectedSection >= book.contents.sections.length) {
      setSelectedSection(0);
    }
  }, [book, selectedSection]);

  // 전체화면 모달 상태에 따라 body 스크롤 제어
  useEffect(() => {
    if (showFullscreenModal) {
      // 모달이 열릴 때 body 스크롤 비활성화
      document.body.classList.add('fullscreenModalOpen');
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '0px'; // 스크롤바 너비만큼 패딩 제거
      document.documentElement.style.overflow = 'hidden'; // html 요소도 스크롤 비활성화
    } else {
      // 모달이 닫힐 때 body 스크롤 복원
      document.body.classList.remove('fullscreenModalOpen');
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '';
      document.documentElement.style.overflow = 'unset';
    }

    // 컴포넌트 언마운트 시 스크롤 복원
    return () => {
      document.body.classList.remove('fullscreenModalOpen');
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '';
      document.documentElement.style.overflow = 'unset';
    };
  }, [showFullscreenModal]);

  // 섹션 변경 시 페이지 초기화
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedSection]);

  // 초기 로딩 시 표지 페이지 표시
  useEffect(() => {
    if (book && currentPage === -2) {
      setCurrentPage(-2);
    }
  }, [book, currentPage]);

  // 랜드스케이프 모드 감지
  useEffect(() => {
    const checkLandscape = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const aspectRatio = width / height;
      
      // 더 유연한 랜드스케이프 모드 감지
      const isLandscapeMode = (width >= 1400 && aspectRatio >= 1.5) || (width >= 1600 && aspectRatio >= 1.33);
      
      setIsLandscape(isLandscapeMode);
    };

    checkLandscape();
    window.addEventListener('resize', checkLandscape);
    
    return () => window.removeEventListener('resize', checkLandscape);
  }, []);


  // 페이지 계산
  useEffect(() => {
    if (book?.contents?.sections?.[selectedSection]?.paragraphs) {
      const paragraphs = book.contents.sections[selectedSection].paragraphs;
      const totalWords = paragraphs.reduce((acc, paragraph) => acc + paragraph.length, 0);
      // 대략적으로 300단어당 1페이지로 계산
      setPagesPerSection(Math.max(1, Math.ceil(totalWords / 300)));
    }
  }, [book, selectedSection]);

  useEffect(() => {
    if (!userId || !projectId || !bookId) {
      navigate('/admin/users');
      return;
    }

    const fetchBook = async () => {
      try {
        setLoading(true);
        const response = await getDigitalBookInfo(userId, projectId, bookId);
        console.log('API 응답:', response);
        console.log('응답 타입:', typeof response);
        console.log('응답 키들:', Object.keys(response));
        
        // response가 이미 파싱된 데이터인지 확인
        if (response && typeof response === 'object' && response.book) {
          setBook(response.book);
          setError(null);
        } else {
          console.error('예상치 못한 응답 형식:', response);
          setError('API 응답 형식이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('Error fetching book:', error);
        setError('책 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [userId, projectId, bookId, navigate, getDigitalBookInfo]);

  const handleEditSection = (sectionIndex: number) => {
    const chapter = book?.contents?.sections?.[sectionIndex];
    if (chapter && chapter.paragraphs) {
      setEditingSection(sectionIndex);
      setEditedContent(chapter.paragraphs.join('\n\n'));
    }
  };

  const handleSaveSection = () => {
    if (editingSection !== null && book?.contents?.sections) {
      const updatedBook = { ...book };
      if (updatedBook.contents.sections[editingSection]) {
        
        updatedBook.contents.sections[editingSection].paragraphs = editedContent.split('\n\n').filter(p => p.trim());

        const editedSection = updatedBook.contents.sections[editingSection];

        editDigitalBookSection(userId!, projectId!, bookId!, editingSection, editedSection);

        setBook(updatedBook);
        setEditingSection(null);
        setEditedContent('');
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditedContent('');
  };

  const handleDeleteSection = (sectionIndex: number) => {
    setConfirmAction(() => () => {
      // if (book?.contents?.sections) {
      //   const updatedBook = { ...book };
      //   updatedBook.contents.chapters.splice(sectionIndex, 1);
      //   // 인덱스 재정렬
      //   updatedBook.contents.chapters.forEach((chapter, index) => {
      //     chapter.index = index;
      //   });
      //   setBook(updatedBook);
      //   if (selectedSection >= sectionIndex) {
      //     setSelectedSection(Math.max(0, selectedSection - 1));
      //   }
      // }
      console.log('섹션 삭제 확인됨:', sectionIndex);
      setShowConfirm(false);
    });
    setShowConfirm(true);
  };

  const handleRegenerateSection = (sectionIndex: number) => {
    // 섹션 재생성 로직 (API 호출 필요)
    console.log('Regenerate chapter:', sectionIndex);
    // 섹션 재생성 로직 (API 호출 필요)
    console.log('섹션 재생성 확인됨:', sectionIndex);
  };

  // 관리 기능 함수들
  const generateRandomPasscode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleDeleteBook = () => {
    // book이 null인 경우에도 삭제 진행 가능하도록 수정
    if (!userId || !projectId || !bookId) {
      console.error('필수 정보가 누락되었습니다:', { userId, projectId, bookId });
      return;
    }
    
    console.log('handleDeleteBook 실행됨');
    const newPasscode = generateRandomPasscode();
    console.log('생성된 패스코드:', newPasscode);
    setGeneratedPasscode(newPasscode);
    setShowDeleteConfirm(true);
    setPasscode('');
    setPasscodeError('');
    console.log('showDeleteConfirm을 true로 설정함');
  };

  const handleRewriteBook = () => {
    const newPasscode = generateRandomPasscode();
    setGeneratedPasscode(newPasscode);
    setShowRewriteConfirm(true);
    setPasscode('');
    setPasscodeError('');
  };

  const validatePasscode = (inputPasscode: string) => {
    // 입력된 패스코드와 생성된 패스코드를 대소문자 구분 없이 비교
    return inputPasscode.toUpperCase() === generatedPasscode.toUpperCase();
  };

  // 이전 페이지 정보 가져오기
  const fromPage = location.state?.from;
  const fromPageName = location.state?.fromName;

  // 뒤로가기 처리
  const handleGoBack = () => {
    console.log('handleGoBack called');
    console.log('fromPage:', fromPage);
    console.log('userId:', userId);
    
    let targetPath: string;
    
    if (fromPage && fromPage !== `/admin/users/detail/${userId}`) {
      console.log('Navigating to fromPage:', fromPage);
      targetPath = fromPage;
    } else {
      console.log('Navigating to user detail:', `/admin/users/detail/${userId}`);
      targetPath = `/admin/users/detail/${userId}`;
    }
    
    try {
      navigate(targetPath);
      console.log('navigate function called successfully');
    } catch (error) {
      console.error('navigate function failed:', error);
      console.log('Falling back to window.location.href');
      window.location.href = targetPath;
    }
  };

  // 뒤로가기 버튼 텍스트 결정
  const getBackButtonText = () => {
    if (fromPage && fromPage !== `/admin/users/detail/${userId}`) {
      return `← ${fromPageName || '이전 페이지'}로 돌아가기`;
    }
    return '← 목록으로 돌아가기';
  };

  const handleConfirmDelete = async () => {
    if (!passcode.trim()) {
      setPasscodeError('패스코드를 입력해주세요.');
      return;
    }

    if (!validatePasscode(passcode)) {
      setPasscodeError('패스코드가 올바르지 않습니다.');
      return;
    }

    if (!userId || !projectId || !bookId) {
      setPasscodeError('필수 정보가 누락되었습니다.');
      return;
    }

    try {
      // 실제 삭제 API 호출
      await deleteDigitalBook(userId, projectId, bookId);
      
      console.log('책 삭제 성공:', bookId);
      setShowDeleteConfirm(false);
      setPasscode('');
      setPasscodeError('');
      
      // 성공 후 이전 페이지로 이동
      handleGoBack();
    } catch (error) {
      console.error('책 삭제 실패:', error);
      setPasscodeError('책 삭제 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const handleConfirmRewrite = async () => {
    if (!passcode.trim()) {
      setPasscodeError('패스코드를 입력해주세요.');
      return;
    }

    if (!validatePasscode(passcode)) {
      setPasscodeError('패스코드가 올바르지 않습니다.');
      return;
    }

    if (!userId || !projectId || !bookId) {
      setPasscodeError('필수 정보가 누락되었습니다.');
      return;
    }

    try {
      // 1. 먼저 책 삭제 API 호출
      await deleteDigitalBook(userId, projectId, bookId);
      console.log('책 삭제 성공:', book?.id);
      
      // 2. 삭제 성공 시 즉시 책 다시 쓰기 API 호출
      await rewriteDigitalBook(userId, parseInt(projectId));
      console.log('책 다시 쓰기 요청 성공');
      
      setShowRewriteConfirm(false);
      setPasscode('');
      setPasscodeError('');
      
      // 성공 후 이전 페이지로 이동
      handleGoBack();
    } catch (error) {
      console.error('책 삭제 및 다시 쓰기 실패:', error);
      setPasscodeError('책 삭제 및 다시 쓰기 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const closeConfirmModal = () => {
    setShowDeleteConfirm(false);
    setShowRewriteConfirm(false);
    setShowCreatePhysicalBookOrderModal(false);
    setPasscode('');
    setPasscodeError('');
  };

  // 실물책 주문 생성 모달 열기
  const handleOpenCreateOrderModal = () => {
    const newPasscode = generateRandomPasscode();
    setCreateOrderPasscode(newPasscode);
    setCreateOrderPasscodeInput('');
    setShowCreatePhysicalBookOrderModal(true);
  };

  // 실물책 주문 생성 패스코드 검증
  const handleCreateOrderPasscodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const filteredInput = input.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    setCreateOrderPasscodeInput(filteredInput);
  };

  // 실물책 주문 생성 패스코드 검증
  const isCreateOrderPasscodeValid = () => {
    return createOrderPasscodeInput.trim().length === 4 && 
          createOrderPasscodeInput.toUpperCase() === createOrderPasscode.toUpperCase();
  };

  // 전자책 PDF 생성
  const handleGenDigitalBookPDF = async () => {
    if (!userId || !projectId || !bookId) {
      setToastMessage('필수 정보가 누락되었습니다.');
      setToastType('error');
      setToastIsVisible(true);
      return;
    }

    try {
      setLoading(true);
      
      // ApiProvider의 genDigitalBookPDF API 사용
      const response = await genDigitalBookPDF(userId, projectId, bookId);
      
      // 응답이 올바른지 확인
      if (!response) {
        throw new Error('PDF 생성 응답이 올바르지 않습니다.');
      }
      
      setToastMessage('전자책 PDF가 성공적으로 생성되었습니다.');
      setToastType('success');
      setToastIsVisible(true);
      
    } catch (err) {
      console.error('전자책 PDF 생성 실패:', err);
      setToastMessage(err instanceof Error ? err.message : 'PDF 생성 중 오류가 발생했습니다.');
      setToastType('error');
      setToastIsVisible(true);
    } finally {
      setLoading(false);
    }
  };

  // 책 다운로드
  const handleDownloadBook = async () => {
    if (!book || !userId || !projectId || !bookId) {
      setToastMessage('필수 정보가 누락되었습니다.');
      setToastType('error');
      setToastIsVisible(true);
      return;
    }
    
    try {
      setLoading(true);
      
      // ApiProvider의 downloadBookContents API 사용
      const response = await downloadBookContents(book.project_id, book.id, false);
      
      // 응답이 올바른지 확인
      if (!response || response.type !== 'application/zip') {
        throw new Error('다운로드한 파일이 올바르지 않습니다.');
      }
      
      // 파일명 생성 (서버 규칙과 동일한 기본값)
      const filename = `p${book.project_id}b${book.id}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '')}.zip`;
      
      // 파일 다운로드 처리
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setToastMessage('전자책 컨텐츠가 성공적으로 다운로드되었습니다.');
      setToastType('success');
      setToastIsVisible(true);
      
    } catch (err) {
      console.error('전자책 컨텐츠 다운로드 실패:', err);
      setToastMessage(err instanceof Error ? err.message : '다운로드 중 오류가 발생했습니다.');
      setToastType('error');
      setToastIsVisible(true);
    } finally {
      setLoading(false);
    }
  };

  // 실물책 주문 생성
  const handleCreatePhysicalBookOrder = async () => {
    if (!book || !projectId || !bookId) return;

    if (!isCreateOrderPasscodeValid()) {
      alert('패스코드가 올바르지 않습니다.');
      return;
    }

    try {
      // 더미 데이터로 주문 생성
      const dummyOrderData = {
        projectId: parseInt(projectId),
        bookId: parseInt(bookId),
        postalCode: '12345',
        address: '서울특별시 강남구 테헤란로',
        addressDetail: '123번길 45',
        recipientName: '홍길동',
        recipientPhone: '010-1234-5678',
        coverStyleId: 'c000',
        image: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==' // 1x1 투명 PNG
      };

      await orderPhysicalBook(dummyOrderData);
      
      alert('실물책 주문이 성공적으로 생성되었습니다.');
      
      // 모달 닫기
      setShowCreatePhysicalBookOrderModal(false);
      setCreateOrderPasscode('');
      setCreateOrderPasscodeInput('');
      
      // 페이지 새로고침
      window.location.reload();
      
    } catch (err) {
      console.error('실물책 주문 생성 실패:', err);
      alert(err instanceof Error ? err.message : '실물책 주문 생성 중 오류가 발생했습니다.');
    }
  };

  // 패스코드 입력 처리 - 영어/숫자만 허용하고 자동으로 대문자 변환
  const handlePasscodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // 영어와 숫자만 허용하고 자동으로 대문자 변환
    const filteredInput = input.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    setPasscode(filteredInput);
    setPasscodeError(''); // 입력 시 에러 메시지 제거
  };

  // 패스코드가 완전히 일치하는지 확인하는 함수
  const isPasscodeValid = () => {
    return passcode.trim().length === 4 && validatePasscode(passcode);
  };

  // PDF 다운로드 핸들러
  const handlePdfDownload = () => {
    if (book?.contents?.pdf_download_url) {
      const link = document.createElement('a');
      link.href = book.contents.pdf_download_url;
      link.download = `${book.contents?.title || '전자책'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setToastMessage('PDF 파일이 다운로드되었습니다.');
      setToastType('success');
      setToastIsVisible(true);
    } else {
      setToastMessage('PDF 파일을 찾을 수 없습니다.');
      setToastType('error');
      setToastIsVisible(true);
    }
  };

  // 전체화면 모달 열기
  const handleOpenFullscreen = () => {
    setShowFullscreenModal(true);
  };

  // 전체화면 모달 닫기
  const handleCloseFullscreen = () => {
    setShowFullscreenModal(false);
    // PDF 뷰어를 완전히 재마운트하여 모든 상태 초기화
    console.log('전체화면 모달 닫기 후 PDF 뷰어 재마운트');
    setTimeout(() => {
      setPdfKey(prev => prev + 1);
    }, 200);
  };

  // 실물책 주문 여부 확인 (실제로는 API에서 데이터를 가져와야 함)
  const hasPhysicalBookOrder = () => {
    // 실물책 주문 여부 확인:
    // 최소된 경우, 도는 주문이 없는 경우에만 삭제할 수 있습니다.
    const physical_book_order = book?.physical_book_order;
    if (physical_book_order) {
      if (physical_book_order.delivery_state !== 'CANCELLED') {
        return true;
      }
    }
    return false;
  };

  // 페이지 넘기기 함수들
  const goToPreviousPage = () => {
    if (currentPage > 0) {
      // 랜드스케이프 모드에서는 2페이지씩 이동
      const pageDecrement = isLandscape ? 2 : 1;
      setCurrentPage(Math.max(currentPage - pageDecrement, 0));
    } else if (currentPage === 0 && selectedSection > 0) {
      setSelectedSection(selectedSection - 1);
      setCurrentPage(pagesPerSection - 1);
    } else if (currentPage === 0 && selectedSection === 0) {
      setCurrentPage(-1); // 첫 번째 섹션의 첫 페이지에서 이전으로 가면 목차로
    } else if (currentPage === -1) {
      setCurrentPage(-2); // 목차에서 이전으로 가면 표지로
    }
  };

  const goToNextPage = () => {
    if (currentPage === -2) {
      // 표지에서 다음으로 가면 목차로
      setCurrentPage(-1);
    } else if (currentPage === -1) {
      // 목차에서 다음으로 가면 첫 번째 섹션의 첫 페이지로
      setSelectedSection(0);
      setCurrentPage(0);
    } else if (currentPage < pagesPerSection - 1) {
      // 랜드스케이프 모드에서는 2페이지씩 이동
      const pageIncrement = isLandscape ? 2 : 1;
      setCurrentPage(Math.min(currentPage + pageIncrement, pagesPerSection - 1));
    } else if (currentPage === pagesPerSection - 1 && selectedSection < (book?.contents?.sections?.length || 0) - 1) {
      setSelectedSection(selectedSection + 1);
      setCurrentPage(0);
    }
  };

  const goToPreviousSection = () => {
    if (currentPage === -2 || currentPage === -1) {
      // 표지나 목차에서는 이전 섹션로 이동할 수 없음
      return;
    }
    if (selectedSection > 0) {
      setSelectedSection(selectedSection - 1);
      setCurrentPage(0);
    } else {
      setCurrentPage(-1); // 첫 번째 섹션에서 이전 섹션로 가면 목차로
    }
  };

  const goToNextSection = () => {
    if (currentPage === -2) {
      // 표지에서 다음 섹션로 가면 첫 번째 섹션로
      setSelectedSection(0);
      setCurrentPage(0);
    } else if (currentPage === -1) {
      // 목차에서 다음 섹션로 가면 첫 번째 섹션로
      setSelectedSection(0);
      setCurrentPage(0);
    } else if (selectedSection < (book?.contents?.sections?.length || 0) - 1) {
      setSelectedSection(selectedSection + 1);
      setCurrentPage(0);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'draft': return styles.statusBadge + ' ' + styles.default;
      case 'published': return styles.statusBadge + ' ' + styles.completed;
      case 'review': return styles.statusBadge + ' ' + styles.inProgress;
      default: return styles.statusBadge + ' ' + styles.default;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return '초안';
      case 'published': return '출간됨';
      case 'review': return '검토중';
      default: return status;
    }
  };

  // 현재 페이지의 내용을 계산
  const getCurrentPageContent = () => {
    if (!book?.contents?.sections?.[selectedSection]?.paragraphs) return [];
    
    const paragraphs = book.contents.sections[selectedSection].paragraphs;
    const wordsPerPage = isLandscape ? 200 : 300; // 랜드스케이프 모드에서는 페이지당 단어 수 줄임
    const startIndex = currentPage * wordsPerPage;
    const endIndex = startIndex + wordsPerPage;
    
    // 단순한 단어 기반 페이지 분할
    let currentWords = 0;
    const pageContent = [];
    
    for (const paragraph of paragraphs) {
      const paragraphWords = paragraph.split(' ').length;
      if (currentWords + paragraphWords > startIndex && currentWords < endIndex) {
        pageContent.push(paragraph);
      }
      currentWords += paragraphWords;
      if (currentWords >= endIndex) break;
    }
    
    return pageContent;
  };

  // 랜드스케이프 모드에서 2페이지 표시를 위한 추가 페이지 내용
  const getNextPageContent = () => {
    if (!isLandscape || !book?.contents?.sections?.[selectedSection]?.paragraphs) return [];
    
    const paragraphs = book.contents.sections[selectedSection].paragraphs;
    const wordsPerPage = 200;
    const startIndex = (currentPage + 1) * wordsPerPage;
    const endIndex = startIndex + wordsPerPage;
    
    let currentWords = 0;
    const pageContent = [];
    
    for (const paragraph of paragraphs) {
      const paragraphWords = paragraph.split(' ').length;
      if (currentWords + paragraphWords > startIndex && currentWords < endIndex) {
        pageContent.push(paragraph);
      }
      currentWords += paragraphWords;
      if (currentWords >= endIndex) break;
    }
    
    return pageContent;
  };

  if (loading) {
    return (
      <div className={styles.userDetail}>
        <div className={styles.loadingSpinner}>책 정보를 불러오는 중...</div>
      </div>
    );
  }

  const renderModals = () => (
    <>
      {/* 확인 모달 */}
      {showConfirm && (
        <Confirm
          show={showConfirm}
          onClose={(result) => {
            if (result === 'Yes' && confirmAction) {
              confirmAction();
            }
            setShowConfirm(false);
          }}
          title="섹션 삭제"
          message="정말로 이 섹션를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다."
          confirmText="삭제"
          cancelText="취소"
        />
      )}

      {/* 책 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmModal}>
            <div className={styles.modalHeader}>
              <h3>🗑️ 책 삭제 확인</h3>
              <button 
                onClick={closeConfirmModal}
                className={styles.modalCloseButton}
                title="닫기"
              >
                ×
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.modalWarning}>
                <span className={styles.modalWarningIcon}>⚠️</span>
                <p>이 책을 완전히 삭제하시겠습니까?</p>
                <p className={styles.modalWarningDetail}>
                  이 작업은 되돌릴 수 없으며, 모든 섹션와 내용이 영구적으로 삭제됩니다.
                </p>
              </div>
              <div className={styles.passcodeSection}>
                <div className={styles.passcodeDisplay}>
                  <label>확인 패스코드:</label>
                  <div className={styles.generatedPasscode}>{generatedPasscode}</div>
                </div>
                <label htmlFor="deletePasscode">패스코드 입력:</label>
                <input
                  id="deletePasscode"
                  type="text"
                  value={passcode}
                  onChange={handlePasscodeChange}
                  placeholder="영문/숫자 4자리 입력"
                  className={styles.passcodeInput}
                  onKeyPress={(e) => e.key === 'Enter' && handleConfirmDelete()}
                  maxLength={4}
                  autoComplete="off"
                  spellCheck="false"
                  inputMode="text"
                />
                {passcodeError && (
                  <div className={styles.passcodeError}>{passcodeError}</div>
                )}
              </div>
            </div>
            <div className={styles.modalActions}>
              <button
                onClick={closeConfirmModal}
                className={styles.modalCancelButton}
              >
                취소
              </button>
              <button
                onClick={handleConfirmDelete}
                className={styles.modalConfirmButton}
                disabled={!isPasscodeValid()}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 책 다시 쓰기 확인 모달 */}
      {showRewriteConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmModal}>
            <div className={styles.modalHeader}>
              <h3>✍️ 책 다시 쓰기 확인</h3>
              <button 
                onClick={closeConfirmModal}
                className={styles.modalCloseButton}
                title="닫기"
              >
                ×
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.modalWarning}>
                <span className={styles.modalWarningIcon}>⚠️</span>
                <p>이 책을 처음부터 다시 작성하시겠습니까?</p>
                <p className={styles.modalWarningDetail}>
                  기존 내용은 모두 삭제되고 새로운 내용으로 대체됩니다.
                </p>
              </div>
              <div className={styles.passcodeSection}>
                <div className={styles.passcodeDisplay}>
                  <label>확인 패스코드:</label>
                  <div className={styles.generatedPasscode}>{generatedPasscode}</div>
                </div>
                <label htmlFor="rewritePasscode">패스코드 입력:</label>
                <input
                  id="rewritePasscode"
                  type="text"
                  value={passcode}
                  onChange={handlePasscodeChange}
                  placeholder="영문/숫자 4자리 입력"
                  className={styles.passcodeInput}
                  onKeyPress={(e) => e.key === 'Enter' && handleConfirmRewrite()}
                  maxLength={4}
                  autoComplete="off"
                  spellCheck="false"
                  inputMode="text"
                />
                {passcodeError && (
                  <div className={styles.passcodeError}>{passcodeError}</div>
                )}
              </div>
            </div>
            <div className={styles.modalActions}>
              <button
                onClick={closeConfirmModal}
                className={styles.modalCancelButton}
              >
                취소
              </button>
              <button
                onClick={handleConfirmRewrite}
                className={`${styles.modalConfirmButton} ${styles.rewriteConfirmButton}`}
                disabled={!isPasscodeValid()}
              >
                다시 쓰기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 전체화면 PDF 뷰어 모달 */}
      {showFullscreenModal && book?.contents?.pdf_download_url && (
        <div className={styles.fullscreenModal}>
          <div className={styles.fullscreenHeader}>
            <h3>{book.contents?.title || '전자책'} - 전체화면</h3>
            <button
              onClick={handleCloseFullscreen}
              className={styles.fullscreenCloseButton}
              title="전체화면 닫기"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className={styles.fullscreenContent}>
            <PDFViewer 
              key={`fullscreen-${pdfKey}`}
              url={book.contents.pdf_download_url}
              title={book.contents?.title || '전자책'}
              onError={(error) => {
                console.error('전체화면 PDF 뷰어 오류:', error);
                setToastMessage('전체화면에서 PDF를 로드하는 중 오류가 발생했습니다.');
                setToastType('error');
                setToastIsVisible(true);
              }}
              onDownload={handlePdfDownload}
              showControls={true}
              isFullscreen={true}
            />
          </div>
        </div>
      )}
    </>
  );

  if (error || !book) {
    return (
      <div className={styles.userDetail}>
        <div className={styles.errorMessage}>
          <p>오류가 발생했습니다: {error || '책을 찾을 수 없습니다.'}</p>
          <div className={styles.errorActions}>
            <button onClick={handleGoBack} className={styles.btnBack}>
              {getBackButtonText()}
            </button>
            <button
              onClick={() => {
                console.log('책 삭제 버튼 클릭됨');
                handleDeleteBook();
              }}
              className={`${styles.managementButton} ${styles.deleteButton} ${styles.deleteButtonStyle}`}
              title="이 책을 완전히 삭제합니다"
            >
              <DeleteIcon />
              책 삭제
            </button>
          </div>
        </div>
        {renderModals()}
      </div>
    );
  }

  return (
    <div className={styles.userDetail}>
      {/* 페이지 헤더 */}
      <div className={styles.pageTitleRow}>
        <h1>📚 디지털 책 상세</h1>
        <button 
          onClick={handleGoBack} 
          className={styles.btnBack}
        >
          {getBackButtonText()}
        </button>
      </div>

      {/* 전자책 뷰어 */}
      <div className={styles.userInfoSection}>
        <div className={styles.sectionHeader}>
          <h2>📖 전자책 뷰어</h2>
          <div className={styles.sectionHeaderActions}>
            <span className={styles.statusBadge + ' ' + styles.completed}>
              {book.contents?.pdf_download_url ? 'PDF 뷰어' : `${book.contents?.sections?.length || 0}개 섹션`}
            </span>
            {book.contents?.pdf_download_url && (
              <div className={styles.pdfViewerActions}>
                <button
                  onClick={handlePdfDownload}
                  className={styles.pdfActionButton}
                  title="PDF 다운로드"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 16l-4-4h8l-4 4zm0 0V4m-8 13v3h16v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  다운로드
                </button>
                <button
                  onClick={handleOpenFullscreen}
                  className={styles.pdfActionButton}
                  title="전체화면으로 보기"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  전체화면
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={`${styles.ebookContainer} ${isLandscape ? styles.landscape : ''}`}>
          {book.contents?.pdf_download_url ? (
            <PDFViewer 
              key={pdfKey}
              url={book.contents.pdf_download_url}
              title={book.contents?.title || '전자책'}
              onError={(error) => {
                console.error('PDF 뷰어 오류:', error);
                console.error('PDF URL:', book.contents.pdf_download_url);
                setToastMessage('PDF를 로드하는 중 오류가 발생했습니다.');
                setToastType('error');
                setToastIsVisible(true);
              }}
            />
          ) : book.contents?.sections && book.contents.sections.length > 0 ? (
            editingSection === selectedSection ? (
              <div className={styles.sectionEditMode}>
                <div className={styles.sectionEditInfo}>
                  <EditBookIcon className={styles.editInfoIcon} />
                  섹션 편집 모드 - 내용을 수정한 후 저장하거나 취소할 수 있습니다.
                </div>
                <div className={styles.sectionEditLayout}>
                  <div className={styles.sectionEditInput}>
                    <label>섹션 내용 편집</label>
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      className={styles.sectionTextarea}
                      placeholder="섹션 내용을 입력하세요..."
                    />
                  </div>
                  <div className={styles.sectionEditPreview}>
                    <label>미리보기</label>
                    <div className={styles.sectionPreviewContent}>
                      {editedContent ? (
                        editedContent.split('\n\n').map((paragraph, index) => (
                          <p key={index} className={styles.paragraph}>
                            {paragraph}
                          </p>
                        ))
                      ) : (
                        <div className={styles.chapterPreviewEmpty}>
                          <span className={styles.previewEmptyIcon}>📝</span>
                          편집할 내용을 입력하세요
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.editActions}>
                  <button
                    onClick={handleSaveSection}
                    className={styles.saveButton}
                    title="저장"
                  >
                    <SaveIcon />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className={styles.cancelButton}
                    title="취소"
                  >
                    <CancelIcon />
                  </button>
                </div>
              </div>
            ) : (
              <div className={`${styles.bookReader} ${isLandscape ? styles.landscape : ''}`}>
                {/* 표지, 목차, 또는 책 페이지 */}
                {currentPage === -2 ? (
                  // 표지 페이지
                  <div className={`${styles.bookPage} ${isLandscape ? styles.landscape : ''}`}>
                    <div className={styles.pageHeader}>
                      <div className={styles.pageInfo}>
                        <span className={styles.chapterTitle}>📚 책 표지</span>
                        <span className={styles.pageNumber}>표지</span>
                      </div>
                      <div className={styles.chapterProgress}>
                        {getStatusText(book.status)}
                      </div>
                    </div>
                    
                    <div className={styles.pageContent}>
                      <div className={styles.coverPageContent}>
                        <div className={styles.coverIconContainer}>
                          <BookIcon className={styles.coverIcon} />
                        </div>
                        <h1 className={styles.coverTitle}>{book.contents?.title || '제목 없음'}</h1>
                        <p className={styles.coverAuthor}>저자: {book.contents?.author || '저자 없음'}</p>
                        <div className={styles.coverStatus}>
                          <span className={getStatusBadgeClass(book.status)}>
                            {getStatusText(book.status)}
                          </span>
                        </div>
                        <div className={styles.coverInfo}>
                          <p>총 {book.contents?.sections?.length || 0}개 섹션</p>
                          <p>생성일: {formatDate(book.created_at)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className={styles.pageFooter}>
                      <div className={styles.pageNumberBottom}>표지</div>
                    </div>
                  </div>
                ) : currentPage === -1 ? (
                  // 목차 페이지
                  <div className={`${styles.bookPage} ${isLandscape ? styles.landscape : ''}`}>
                    <div className={styles.pageHeader}>
                      <div className={styles.pageInfo}>
                        <span className={styles.chapterTitle}>📑 목차</span>
                        <span className={styles.pageNumber}>목차</span>
                      </div>
                      <div className={styles.chapterProgress}>
                        총 {book.contents?.sections?.length || 0}개 섹션
                      </div>
                    </div>
                    
                    <div className={styles.pageContent}>
                      <div className={styles.tocContent}>
                        <h2 className={styles.tocTitle}>📑 목차</h2>
                        <div className={styles.tocBookInfo}>
                          <span className={styles.tocBookTitle}>{book.contents?.title || '제목 없음'}</span>
                          <span className={styles.tocBookAuthor}>저자: {book.contents?.author || '저자 없음'}</span>
                        </div>
                        
                        <div className={styles.tocChapterList}>
                          {book.contents?.sections?.map((section, index) => (
                            <div 
                              key={index}
                              className={`${styles.tocChapterItem} ${selectedSection === index ? styles.tocSelected : ''}`}
                              onClick={() => {
                                setSelectedSection(index);
                                setCurrentPage(0);
                              }}
                            >
                              <div className={styles.tocChapterHeader}>
                                <span className={styles.tocChapterNumber}>{index + 1}</span>
                                <span className={styles.tocChapterTitle}>{section.title}</span>
                              </div>
                              <div className={styles.tocChapterActions}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSection(index);
                                  }}
                                  className={styles.tocIconButton}
                                  title="편집"
                                >
                                  <EditIcon />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRegenerateSection(index);
                                  }}
                                  className={styles.tocIconButton}
                                  title="재생성"
                                >
                                  <RegenerateIcon />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSection(index);
                                  }}
                                  className={styles.tocIconButton}
                                  title="삭제"
                                >
                                  <DeleteIcon />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className={styles.pageFooter}>
                      <div className={styles.pageNumberBottom}>목차</div>
                    </div>
                  </div>
                ) : (
                  // 본문 페이지
                  <>
                    <div className={`${styles.bookPage} ${isLandscape ? styles.landscape : ''}`}>
                      <div className={styles.pageHeader}>
                        <div className={styles.pageInfo}>
                          <span className={styles.chapterTitle}>
                            {book.contents.sections[selectedSection]?.title || '제목 없음'}
                          </span>
                          <span className={styles.pageNumber}>
                            {currentPage + 1} / {pagesPerSection}
                          </span>
                        </div>
                        <div className={styles.chapterProgress}>
                          섹션 {selectedSection + 1} / {book.contents.sections.length}
                        </div>
                      </div>
                      
                      <div className={styles.pageContent}>
                        {getCurrentPageContent().map((paragraph, index) => (
                          <p key={index} className={styles.pageParagraph}>
                            {paragraph}
                          </p>
                        ))}
                      </div>
                      
                      <div className={styles.pageFooter}>
                        <div className={styles.pageNumberBottom}>
                          {currentPage + 1}
                        </div>
                      </div>
                    </div>
                    
                    {/* 랜드스케이프 모드에서 오른쪽 페이지 표시 */}
                    {isLandscape && currentPage < pagesPerSection - 1 && (
                      <div className={`${styles.bookPage} ${styles.landscape}`}>
                        <div className={styles.pageHeader}>
                          <div className={styles.pageInfo}>
                            <span className={styles.chapterTitle}>
                              {book.contents.sections[selectedSection]?.title || '제목 없음'}
                            </span>
                            <span className={styles.pageNumber}>
                              {currentPage + 2} / {pagesPerSection}
                            </span>
                          </div>
                          <div className={styles.chapterProgress}>
                            섹션 {selectedSection + 1} / {book.contents.sections.length}
                          </div>
                        </div>
                        
                        <div className={styles.pageContent}>
                          {getNextPageContent().map((paragraph, index) => (
                            <p key={index} className={styles.pageParagraph}>
                              {paragraph}
                            </p>
                          ))}
                        </div>
                        
                        <div className={styles.pageFooter}>
                          <div className={styles.pageNumberBottom}>
                            {currentPage + 2}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
              </div>
            )
          ) : (
            <div className={styles.noChapters}>
              <p>아직 섹션가 없습니다.</p>
            </div>
          )}
        </div>
        
        {/* 페이지 넘기기 컨트롤 - PDF가 없을 때만 표시 */}
        {!book.contents?.pdf_download_url && book.contents?.sections && book.contents.sections.length > 0 && editingSection !== selectedSection && (
          <div className={styles.pageControls}>
            <div className={styles.navigationButtons}>
              <button
                onClick={goToPreviousSection}
                className={`${styles.navButton} ${styles.chapterNav}`}
                disabled={currentPage === -1}
                title="이전 섹션"
              >
                <ChevronLeftIcon />
                이전 섹션
              </button>
              
              <button
                onClick={goToPreviousPage}
                className={`${styles.navButton} ${styles.pageNav}`}
                title="이전 페이지"
              >
                <ChevronLeftIcon />
                이전
              </button>
              
              <button
                onClick={goToNextPage}
                className={`${styles.navButton} ${styles.pageNav}`}
                title="다음 페이지"
              >
                다음
                <ChevronRightIcon />
              </button>
              
              <button
                onClick={goToNextSection}
                className={`${styles.navButton} ${styles.chapterNav}`}
                disabled={currentPage === -1}
                title="다음 섹션"
              >
                다음 섹션
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* 책 기본 정보 */}
      <div className={styles.userInfoSection}>
        <div className={styles.sectionHeader}>
          <div className="flex w-full justify-between items-center">
            <h2>📚 책 정보</h2>
              <div className="ml-auto">
              <button className={styles.downloadButton} onClick={handleDownloadBook}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 16l-4-4h8l-4 4zm0 0V4m-8 13v3h16v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                전자책 콘텐츠 다운로드
              </button>
            </div>
          </div>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <label>책 ID</label>
            <span>{book.id}</span>
          </div>
          <div className={styles.infoItem}>
            <label>프로젝트 ID</label>
            <span>{book.project_id}</span>
          </div>
          <div className={styles.infoItem}>
            <label>작업 ID</label>
            <span>{book.job_id}</span>
          </div>
          <div className={styles.infoItem}>
            <label>상태</label>
            <span className={getStatusBadgeClass(book.status)}>
              {getStatusText(book.status)}
            </span>
          </div>
          <div className={styles.infoItem}>
            <label>제목</label>
            <span>{book.contents?.title || '제목 없음'}</span>
          </div>
          <div className={styles.infoItem}>
            <label>저자</label>
            <span>{book.contents?.author || '저자 없음'}</span>
          </div>
          <div className={styles.infoItem}>
            <label>생성일</label>
            <span>{formatDate(book.created_at)}</span>
          </div>
          <div className={styles.infoItem}>
            <label>수정일</label>
            <span>{formatDate(book.updated_at)}</span>
          </div>
        </div>
      </div>

      {/* 책 관리 기능 */}
      <div className={styles.userInfoSection}>
        <div className={styles.sectionHeader}>
          <h2>⚙️ 책 관리</h2>
        </div>
        <div className={styles.bookManagementActions}>
          <div className={styles.managementWarning}>
            <span className={styles.warningIcon}>⚠️</span>
            <span className={styles.warningText}>
              {hasPhysicalBookOrder() 
                ? '실물책 주문이 있어 관리 기능이 제한됩니다.' 
                : '주의: 이 작업들은 되돌릴 수 없습니다.'}
            </span>
          </div>
          <div className={styles.managementButtons}>
            <button
              onClick={handleDeleteBook}
              className={`${styles.managementButton} ${styles.deleteButton} ${styles.deleteButtonStyle}`}
              disabled={hasPhysicalBookOrder()}
              title={hasPhysicalBookOrder() ? '실물책 주문이 있어 삭제할 수 없습니다' : '이 책을 완전히 삭제합니다'}
            >
              <DeleteIcon />
              삭제
            </button>
            <button
              onClick={handleRewriteBook}
              className={`${styles.managementButton} ${styles.rewriteButton}`}
              disabled={hasPhysicalBookOrder()}
              title={hasPhysicalBookOrder() ? '실물책 주문이 있어 다시 쓸 수 없습니다' : '이 책을 처음부터 다시 작성합니다'}
            >
              <span className={styles.sparkleIcon}>✨</span>
              책 삭제 하고 다시 쓰기
            </button>

            {/* 전자책 PDF 생성 */}
            <button
              onClick={handleGenDigitalBookPDF}
              className={`${styles.managementButton} ${styles.rewriteButton}`}
              disabled={loading}
              title={loading ? 'PDF 생성 중...' : '이 책을 전자책 PDF 생성합니다'}
            >
              <span className={styles.sparkleIcon}>✨</span>
              {loading ? 'PDF 생성 중...' : '전자책 PDF 생성'}
            </button>

            <button
              onClick={handleOpenCreateOrderModal}
              className={`${styles.managementButton} ${styles.rewriteButton}`}
              disabled={hasPhysicalBookOrder()}
              title={hasPhysicalBookOrder() ? '실물책 주문이 있어 생성할 수 없습니다' : '이 책을 실물책 주문 생성합니다'}
            >
              <span className={styles.sparkleIcon}>✨</span>
              실물책 주문 생성
            </button>

            <button
              onClick={handleDownloadBook}
              className={`${styles.managementButton} ${styles.rewriteButton}`}
              title={'이 책을 전자책 콘텐츠 다운로드합니다'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 16l-4-4h8l-4 4zm0 0V4m-8 13v3h16v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              전자책 콘텐츠 다운로드
            </button>

          </div>
        </div>
      </div>

      {/* 실물책 주문 생성 모달 */}
      {showCreatePhysicalBookOrderModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmModal}>
            <div className={styles.modalHeader}>
              <h3>✨ 실물책 주문 생성</h3>
              <button 
                onClick={() => setShowCreatePhysicalBookOrderModal(false)}
                className={styles.modalCloseButton}
                title="닫기"
              >
                ×
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.modalWarning}>
                <span className={styles.modalWarningIcon}>⚠️</span>
                <p>이 책의 실물책 주문을 생성하시겠습니까?</p>
                <p className={styles.modalWarningDetail}>
                  생성된 주문은 관리자가 확인 후 처리됩니다.
                </p>
              </div>
              <div className={styles.passcodeSection}>
                <div className={styles.passcodeDisplay}>
                  <label>확인 패스코드:</label>
                  <div className={styles.generatedPasscode}>{createOrderPasscode}</div>
                </div>
                <label htmlFor="createOrderPasscode">패스코드 입력:</label>
                <input
                  id="createOrderPasscode"
                  type="text"
                  value={createOrderPasscodeInput}
                  onChange={handleCreateOrderPasscodeChange}
                  placeholder="영문/숫자 4자리 입력"
                  className={styles.passcodeInput}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreatePhysicalBookOrder()}
                  maxLength={4}
                  autoComplete="off"
                  spellCheck="false"
                  inputMode="text"
                />
              </div>
            </div>
            <div className={styles.modalActions}>
              <button
                onClick={() => setShowCreatePhysicalBookOrderModal(false)}
                className={styles.modalCancelButton}
              >
                취소
              </button>
              <button
                onClick={handleCreatePhysicalBookOrder}
                className={styles.modalConfirmButton}
                disabled={!isCreateOrderPasscodeValid()}
              >
                생성
              </button>
            </div>
          </div>
        </div>
      )}

      {renderModals()}

      {/* 토스트 메시지 */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastIsVisible}
        onClose={() => setToastIsVisible(false)}
      />
    </div>
  );
};

export default DigitalbookDetail;
